// Platform trial for a SAAS licence (OF-168 / D-53) — the card and the header chip
// LicenseDetail shows for `license_class: 'saas'`.
//
// WHY THIS IS NOT A NEW TRIAL SYSTEM. A free Trust Center org is a zero-price saas
// licence with no JWT and no phone-home, so there is nothing on the licence row to
// put a trial clock on. D-53's "try the full platform" trial is the org's
// per-company clock on its ALLOCATION under the hosted platform licence — the same
// license_org_allocations.{is_trial,trial_expires_at,trial_vendor_limit} that
// Sales › Account edits through <TrialPanel> (PATCH /fleet/orgs/:id/trial, with its
// append-only trial_events history). The platform refuses a past-expiry
// entitlement on its own (`entitlements.ts`), so expiry needs no code here either.
// This component only mounts that panel against the allocation GET /trust/saas-trial
// resolved, and says plainly when there is nothing to mount it on.
//
// WHO MAY BE ISSUED ONE. D-53 scopes the platform trial to a FREE org. This card
// mounts for every saas licence - free, trust_base, trust_pro, enterprise, a
// platform-sale bundle, a churned one - and "Issue platform trial" on a paying
// customer's allocation would cap its vendors at 5 and put it into read-only
// enforcement when the clock lapses. So the controls are offered only when the
// saas ledger says tier = free AND status = active; otherwise the clock is shown
// read-only with the reason. (A UI guard, not a server fence: PATCH
// /fleet/orgs/:id/trial is the same endpoint Sales > Account uses, and an operator
// there may deliberately edit a commercial tenant's clock.)
//
// One deliberate difference from the Sales mount: NO "Convert (end trial)". For a
// free org, "converted" means a purchase through checkout, which this server
// attributes natively (D-33); clearing the flag by hand would leave a perpetual
// free non-trial allocation and contradict D-53/D-17 ("the tasting menu still
// starts at Base license").
import React from 'react';
import { FlaskConical } from 'lucide-react';
import { TrialPanel } from '@/components/trial/TrialPanel';
import { AlertBanner } from '@/components/shared/AlertBanner';
import { Btn } from '@/components/shared/primitives';
import { useCanMutate } from '@/context/PersonaProvider';
import { tierLabel, type SaasTrialLookup } from '@/lib/api/trust';

function daysLeft(expiresAt: string): number {
  return Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 86_400_000);
}

/** "On trial · N days left" / "Trial expired" — nothing at all when not on trial. */
export function SaasTrialChip({ allocation }: { allocation: SaasTrialLookup['allocation'] | undefined }) {
  if (!allocation || !allocation.is_trial || !allocation.trial_expires_at) return null;
  const left = daysLeft(allocation.trial_expires_at);
  const expired = left <= 0;
  return (
    <span
      data-testid="saas-trial-chip"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 9999,
        fontSize: 11, fontWeight: 600, letterSpacing: '0.03em',
        background: expired ? 'var(--ps-pill-red-bg)' : 'var(--ps-pill-purple-bg)',
        color: expired ? 'var(--ps-pill-red-tx)' : 'var(--ps-pill-purple-tx)',
      }}
    >
      <FlaskConical size={11} aria-hidden="true" />
      {expired ? 'Trial expired' : `On trial · ${left} day${left !== 1 ? 's' : ''} left`}
    </span>
  );
}

/** D-53: a platform trial is issued to a free org that is still active. */
export function isTrialEligible(tier: string | null | undefined, status: string | null | undefined): boolean {
  return tier === 'free' && status === 'active';
}

const NO_ALLOCATION_COPY: Record<NonNullable<SaasTrialLookup['reason']>, string> = {
  no_tprm7_binding:
    'This licence is not bound to a TPRM7 org yet, so there is no platform allocation to put a trial on. Bind it first.',
  no_hosted_platform_license:
    'This licence server has no hosted platform licence configured (HOSTED_PLATFORM_LICENSE_ID), so there is no platform allocation to put a trial on.',
  no_allocation:
    'This org has no active platform allocation yet, so there is nothing to put a trial on. A self-serve org gets one at signup; a concierge-provisioned org gets one on its first phone-home.',
};

export function SaasTrialSection({
  lookup,
  loading,
  error,
  onRetry,
  onChanged,
  tier,
  status,
}: {
  lookup: SaasTrialLookup | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  onChanged: () => void | Promise<void>;
  /** The saas ledger's tier / status (saas_license_details); null until it has loaded. */
  tier: string | null;
  status: string | null;
}) {
  const canMutate = useCanMutate();
  const eligible = isTrialEligible(tier, status);
  const known = tier !== null && status !== null;
  const sub = eligible
    ? 'Time-boxed full-platform trial for this free org (D-53) · the platform refuses its entitlements on expiry automatically · every change is logged'
    : known
      ? `Read-only here: platform trials are issued to free, active orgs (D-53), and this org is ${tierLabel(tier)} (${status}). Starting or adjusting its clock would cap a paying customer's vendors.`
      : 'Read-only until this licence\'s tier has loaded (platform trials are issued to free orgs only, D-53).';

  let body: React.ReactNode;
  if (error) {
    body = (
      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-header"><span className="card-title">Platform trial</span></div>
        <div className="card-body" style={{ display: 'grid', gap: 10 }}>
          <AlertBanner kind="error">{error}</AlertBanner>
          <div><Btn sm icon="refresh-cw" onClick={onRetry}>Retry</Btn></div>
        </div>
      </div>
    );
  } else if (!lookup) {
    body = loading ? (
      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-header"><span className="card-title">Platform trial</span></div>
        <div className="card-body" style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading…</div>
      </div>
    ) : null;
  } else if (lookup.allocation) {
    body = (
      <TrialPanel
        allocation={lookup.allocation}
        canMutate={canMutate && eligible}
        accent="var(--accent)"
        onChanged={onChanged}
        title="Platform trial"
        sub={sub}
        startLabel="Issue platform trial"
        idleLabel="Not on trial"
        allowConvert={false}
      />
    );
  } else {
    body = (
      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-header"><span className="card-title">Platform trial</span></div>
        <div className="card-body" style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          {NO_ALLOCATION_COPY[lookup.reason ?? 'no_allocation']}
        </div>
      </div>
    );
  }

  return <div data-testid="saas-trial-card">{body}</div>;
}
